import _ from 'lodash'
import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { MedicationFormStatusOptionProps, STORED_ON_SITE } from 'services/medicationForms/constants'

import { flatten, nest } from 'utils/flatnest'
import { createTypeFromEnumValues } from 'utils/typescript'

import { withAppService, withAppServiceProps } from 'services/app'
import { withMedicationFormsService, withMedicationFormsServiceProps } from 'services/medicationForms'
import { getMedicationFormExportCriteria } from 'services/medicationForms/single/selectors'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withPaginationUtils, withPaginationUtilsProps } from 'services/utils/pagination'
import { withRouteUtilsProps, withRouterUtils } from 'services/utils/router'

import { ModalType } from 'modals'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import { TABLE_COLUMNS } from './constants'
import { getTableData } from './ListHelpers'
import ListView from './ListView'

interface FilterProps {
  dateRange?: Moment[]
  medicationFormStatus?: MedicationFormStatusOptionProps
  room?: number
  storedOnSite?: createTypeFromEnumValues<STORED_ON_SITE>
}

const MEDICATION_FORMS_GROUPS = {
  read: [
    'medicationForm',
    'medicationForm.medicationDetails',
    'medicationForm:medicationDetails',
    'medicationDetails',
    'medicationForm.child',
    'child',
  ],
}

type ListContainerProps = withMedicationFormsServiceProps
  & withAppServiceProps
  & withRouteUtilsProps
  & withModalServiceProps
  & withPaginationUtilsProps
  & withRouterProps

const mapState = (state: RootState, {
  appSelectors,
  medicationFormsListState,
  medicationFormsSelectors,
}: ListContainerProps) => ({
  errorMessages: appSelectors.getErrorMessages(medicationFormsListState),
  isFetching: appSelectors.getIsFetching(medicationFormsListState),
  medicationFormsList: medicationFormsSelectors.getMedicationFormsListSelector(state),
  totalResults: appSelectors.getTotalResults(medicationFormsListState),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

const ListContainer: React.FC<ListContainerProps & PropsFromRedux> = ({
  errorMessages,
  isFetching,
  location,
  medicationFormsActions,
  medicationFormsList,
  medicationFormsSelectors,
  modalActions,
  paginationUtils,
  params,
  setLocationQuery,
  totalResults,
}) => {
  const { childId } = params || {}
  const { query } = location
  const {
    dateRange: dateRangeQuery,
    medicationFormStatus: medicationFormStatusQuery,
    room: roomQuery,
    storedOnSite: storedOnSiteQuery,
  } = nest(query)

  const [search, setSearch] = useState<string>()
  const [filters, setFilters] = useState<FilterProps>({
    dateRange: [dateRangeQuery?.after, dateRangeQuery?.before],
    medicationFormStatus: medicationFormStatusQuery || null,
    room: roomQuery || null,
    storedOnSite: storedOnSiteQuery || null,
  })
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const { getPaginationDetails, onPageChange, page } = paginationUtils

  const isChildContext = !!childId

  const fetchData = () => {
    const { dateRange, medicationFormStatus, room, storedOnSite } = filters

    const locationQuery = {
      dateRange: {
        after: dateRange?.[0] && moment(dateRange?.[0])?.format(DEFAULT_DATE_FORMAT),
        before: dateRange?.[1] && moment(dateRange?.[1])?.format(DEFAULT_DATE_FORMAT),
      },
      medicationFormStatus,
      room,
      search,
      storedOnSite,
    }

    setLocationQuery(flatten(locationQuery))

    const criteria = medicationFormsSelectors.getCriteria({
      childId,
      dateRange,
      medicationFormStatus,
      room,
      search,
      storedOnSite,
    })

    medicationFormsActions.list({
      params: {
        criteria,
        groups: MEDICATION_FORMS_GROUPS,
        page,
      },
    })
  }

  useEffect(() => {
    if (!isInitialized) {
      fetchData()
      setIsInitialized(true)
    }

    return () => {
      medicationFormsActions.clearList()
    }
  }, [])

  useEffect(() => {
    if (isInitialized) {
      if (1 === page) {
        fetchData()
      } else {
        onPageChange()(1)
      }
    }
  }, [
    search,
    filters.dateRange,
    filters.medicationFormStatus,
    filters.room,
    filters.storedOnSite,
  ])

  useEffect(() => {
    if (isInitialized) {
      fetchData()
    }
  }, [page])

  const tableColumns = useMemo(() => {
    if (childId) {
      return TABLE_COLUMNS.filter(({ field }) => 'child' !== field)
    }

    return TABLE_COLUMNS
  }, [childId])

  const handleExportModalClick = (id: number) => (body, onSuccess, onFailed) => {
    const userCriteria = _.find(body?.criteria, { field: 'membership' })

    const exportBody = getMedicationFormExportCriteria({
      id,
      membershipId: userCriteria?.value,
    })

    medicationFormsActions.exportMedicationForm({
      body: exportBody,
      onFailed,
      onSuccess,
    })
  }

  const handleExportClick = (id: number) => {
    modalActions.show<ModalType.EXPORT_REPORT>(ModalType.EXPORT_REPORT, {
      onSendReport: handleExportModalClick(id),
      successMessageTransKey: 'module:Safeguarding:MedicationForm:List:exportSuccessMessage',
      title: i18n.t('module:Safeguarding:MedicationForm:List:exportTitle'),
      userLabel: i18n.t('module:Safeguarding:MedicationForm:List:exportUserLabel'),
    })
  }

  const medicationFormListTableData = useMemo(() => getTableData(
    medicationFormsList,
    childId,
    handleExportClick,
  ), [
    medicationFormsList,
    childId,
  ])

  const handlePageChange = (newPage) => {
    onPageChange()(newPage)
  }

  const handleDateRangeChange = ([after, before]) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      dateRange: [after, before],
    }))
  }

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch)
  }

  const handleRoomChange = (selectedRoom) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      room: selectedRoom?.value,
    }))
  }

  const handleMedicationFormStatusChange = (selectedStatus) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      medicationFormStatus: selectedStatus?.value,
    }))
  }

  const handleStoredOnSiteChange = (selectedStoredOnSite) => {
    setFilters((oldFilters) => ({
      ...oldFilters,
      storedOnSite: selectedStoredOnSite?.value,
    }))
  }

  return (
    <ListView
      childId={childId}
      dateRange={filters?.dateRange}
      errorMessages={errorMessages}
      isChildContext={isChildContext}
      isLoading={isFetching}
      medicationFormListTableData={medicationFormListTableData}
      medicationFormStatus={filters?.medicationFormStatus}
      pagination={getPaginationDetails(totalResults)}
      room={filters?.room}
      search={search}
      storedOnSite={filters?.storedOnSite}
      tableColumns={tableColumns}
      onDateRangeChange={handleDateRangeChange}
      onMedicationFormStatusChange={handleMedicationFormStatusChange}
      onPageChange={handlePageChange}
      onRoomChange={handleRoomChange}
      onSearchChange={handleSearchChange}
      onStoredOnSiteChange={handleStoredOnSiteChange}
    />
  )
}

const enhance = compose(
  withAppService,
  withMedicationFormsService,
  withModalService,
  withRouter,
  withRouterUtils,
  withPaginationUtils,
  connector,
)

export default enhance(ListContainer)
